import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/** A file stored as a base64 data URL so it survives localStorage serialisation. */
export interface UploadedFile {
  name: string
  dataUrl: string
  mimeType: string
}

/** Per-event graphic customisation applied to the public page. */
export interface EventBranding {
  logo?: UploadedFile
  /** CSS colour string */
  primary: string
  background: string
  text: string
  accent: string
  /** Google Fonts family name (or empty string for system default) */
  headingFont: string
  bodyFont: string
}

/** Full event record. */
export interface AppEvent {
  id: string
  title: string
  status: 'draft' | 'published'
  /** Human-readable address — linked to Google Maps on the public page */
  address: string
  /** ISO datetime string */
  startDateTime: string
  endDateTime?: string
  /** Announcement shown on the public event page */
  text: string
  /** Announcement shown on the ticket page */
  ticketAnnouncement?: string
  /** Image (jpg/png/webp/gif) stored as a base64 data URL */
  poster?: UploadedFile
  /** ISO datetime at which the QR code button becomes visible */
  ticketActivation: string
  /** ISO datetime at which registration automatically closes (optional) */
  registrationClosesAt?: string
  /**
   * Manual open/close override.
   * When set, this wins over `registrationClosesAt`.
   * When absent, the deadline (or no restriction) governs.
   */
  registrationManualState?: 'open' | 'closed' | 'hidden'
  /**
   * ISO datetime at which the organizer wants an automatic participation reminder
   * to be sent to all accepted participants.
   *
   * A backend scheduler (`EventReminderService`) polls every minute for events whose
   * `reminderAt` has passed and haven't been reminded yet, then emails every accepted
   * participant and marks the event as sent (fires once per event).
   */
  autoReminderAt?: string
  /** IANA timezone ID (e.g. "Europe/Rome") used to interpret the wall-clock times above */
  timezone?: string
  branding: EventBranding
  createdAt: string
  /**
   * True when the backend returned this event in draft-preview mode — i.e. the
   * event is in DRAFT status but the current user is an authenticated org member
   * and is allowed to view it. The public page shows a "Draft mode" banner when
   * this flag is set.
   */
  draftPreview?: boolean
}

const DEFAULT_BRANDING: EventBranding = {
  primary: '#0A7CFF',
  background: '#FFFFFF',
  text: '#000000',
  accent: '#F2A529',
  headingFont: 'Fraunces',
  bodyFont: 'Sora',
}

interface EventsState {
  list: AppEvent[]
}

const initialState: EventsState = { list: [] }

const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    /** Add a new event (always starts as draft). */
    addEvent(state, action: PayloadAction<AppEvent>) {
      state.list.push(action.payload)
    },

    /** Update editable fields of an existing event. */
    updateEvent(state, action: PayloadAction<Partial<AppEvent> & { id: string }>) {
      const ev = state.list.find((e) => e.id === action.payload.id)
      if (ev) Object.assign(ev, action.payload)
    },

    /** Remove an event entirely. */
    deleteEvent(state, action: PayloadAction<string>) {
      state.list = state.list.filter((e) => e.id !== action.payload)
    },
  },
})

export const { addEvent, updateEvent, deleteEvent } = eventsSlice.actions
export { DEFAULT_BRANDING }
export default eventsSlice.reducer
